import React, { useEffect, useCallback, useState, useRef } from 'react'
import _debounce from 'lodash/debounce';
import './Careers.css'

import { PercentBadgeIcon, HeartIcon, CurrencyEuroIcon, CheckBadgeIcon, CircleStackIcon, BanknotesIcon, FaceSmileIcon, LanguageIcon, ShieldCheckIcon, DocumentMagnifyingGlassIcon } from '@heroicons/react/20/solid'

// Import components
import ContactForm from '../../components/ContactForm';
import JobsHero from '../../components/JobsHero';
import RevenueTile from '../../components/RevenueTile';
import CareerRequirements from '../../components/CareerRequirements';
import Faq from '../../components/Faq';
import Testimonials from '../../components/Testimonials';
import useLargeur from '../../helpers/useLargeur'


function Jobs(props) {
    const largeur = useLargeur()
    const { isEnglish } = props
    const mobileRef = useRef(null)

    const [hasScrolled, setHasScrolled] = useState(false)
    const [loading, setLoading] = useState(false)
    const [value, setValue] = useState(35000);
    const [share, setShare] = useState(30)
    const [minShare, setMinShare] = useState(25)
    const [maxShare, setMaxShare] = useState(35)
    const [parts, setParts] = useState(1)
    const [married, setMarried] = useState(false)
    const [kids, setKids] = useState(0)
    // Valeurs initiales = résultat du calcul pour le cas par défaut (35 000 € ×
    // 30 %, 1 part), identiques au HTML prérendu : partir de null affichait
    // « 0 EUR » au premier rendu et cassait l'hydratation (04/11/2026). Si le
    // barème de modele-social change, mettre à jour ces deux montants.
    const [gross, setGross] = useState(10500)
    const [net, setNet] = useState(6468)
    const minGuaranteed = 3000
    const [netReduced, setNetReduced] = useState(7272)

    // Moteur de calcul (publicodes + modele-social, ~1/2 Mo de JS) chargé au
    // premier calcul seulement, plus dans le bundle de toutes les pages (04/11/2026).
    const debounceEvaluateSalary = async (brut, parts) => {
        const { default: evaluateSalary } = await import('../../helpers/evaluateSalary')
        let resultNet = evaluateSalary(brut, parts, married, false)
        setNet(Math.round(resultNet))

        let resultNetReduced = evaluateSalary(brut, parts, married, true)
        setNetReduced(Math.round(resultNetReduced))
        setLoading(false)
    }

    function handleScroller() {
        if (window.innerWidth < 500 && !hasScrolled) {
            mobileRef?.current?.scrollIntoView({
                behavior: 'smooth'
            })
            setHasScrolled(true)
        }
    }

    const debounceFn = useCallback(_debounce(debounceEvaluateSalary, 500), [])

    function anchorScroll(anchor) {
        document.querySelector(anchor).scrollIntoView({
            behavior: 'smooth'
        });
    }

    // Arrivée avec une ancre (#calculator depuis le hero) : le contenu React
    // n'existe pas encore quand le navigateur tente le saut natif.
    useEffect(() => {
        if (window.location.hash) {
            setTimeout(() => {
                document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' })
            }, 400)
        }
    }, [])

    useEffect(() => {
        if (!localStorage.getItem('careers')) {
            //Effects
            const bounceInUp = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('bounceInUp');
                    }
                });
            });
            bounceInUp.observe(document.querySelector('.jobHero'));
            bounceInUp.observe(document.querySelector('.jobHeroPattern'));
            localStorage.setItem('careers', true)
        }
    }, [])

    // Cas par défaut : le résultat est déjà dans l'état initial (et dans le
    // HTML prérendu). Inutile de charger et de lancer le moteur à l'ouverture
    // de la page : c'était ~2 s de calcul bloquant sur mobile (04/11/2026).
    const casParDefaut = useRef(true)
    useEffect(() => {
        const calculated = value * share / 100
        const effectiveGross = Math.max(calculated, minGuaranteed)
        setGross(effectiveGross)
        if (casParDefaut.current && value === 35000 && share === 30 && parts === 1) return
        casParDefaut.current = false
        debounceFn(effectiveGross, parts)
    }, [value, share, parts, debounceFn])

    useEffect(() => {
        let mesParts = 1
        if (married) {
            mesParts += 1
        }
        if (kids > 0) {
            if (kids <= 2) {
                mesParts += kids * 0.5
            }
            else {
                mesParts += kids - 1
            }
        }
        setParts(mesParts)
    }, [married, kids, debounceFn])

    return (
        <div className='overflow-x-hidden'>
            <div className='pt-32 sm:mt-20 sm:pt-24'>
                <JobsHero
                    isEnglish={isEnglish}
                    benefits={largeur > 1024 ?
                        [
                            isEnglish ? 'Competitive salaries' : 'Salaires compétitifs',
                            isEnglish ? 'Flexible work hours' : 'Horaires de travail flexibles',
                            isEnglish ? '4-day work week' : 'Semaine de 4 jours',
                            isEnglish ? 'Support with relocation' : "Equipe aux petits soins",
                            isEnglish ? 'Benefits for you and your family' : "Qualité de vie pour vous et votre famille",
                            isEnglish ? 'A great work environment' : 'Outil de travail à la pointe',
                        ]
                        :
                        [
                            isEnglish ? 'Competitive salaries' : 'Salaires compétitifs',
                            isEnglish ? 'Flexible work hours' : 'Horaires de travail flexibles',
                            isEnglish ? '5 weeks of paid vacation' : 'Equipe aux petits soins',
                            isEnglish ? 'Support with relocation' : "Qualité de vie pour vous et votre famille",
                        ]}
                    anchorScroll={anchorScroll}
                />
            </div>

            {/* Our job opportunities */}
            <div id="jobs" className='px-4 sm:px-32 mt-24 sm:mt-32'>
                <div className="relative bg-white py-8 sm:py-12 px-4 sm:px-8 shadow-xl  rounded-xl text-center" id="careers">
                    <div className='heroPatternStars absolute bottom-0 left-0 -translate-x-24 translate-y-8 sm:-translate-x-12 sm:translate-y-12 -z-10' />
                    {largeur < 500 && <div className='heroPatternStarsH absolute top-0 right-0 translate-x-32 -translate-y-8 sm:translate-x-12 sm:-translate-y-12 -z-10' />}
                    <h3 className="text-md md:text-xl font-semibold leading-7 text-brand-600">{isEnglish ? "CAREERS IN FRANCE" : "CARRIERES EN FRANCE"}</h3>
                    <h1 className="mb-2 md:mb-8 md:mt-1 text-2xl sm:text-3xl font-bold md:text-4xl tracking-tight text-gray-900">
                        {isEnglish ? "WE ARE HIRING" : 'ON RECRUTE'}
                    </h1>
                    {/* Job cards */}
                    <div className='mt-0 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        {[
                            { title: isEnglish ? 'Dentist' : 'Dentiste', location: 'Châteauroux & Déols', salary: isEnglish ? '6,000 – 20,000 EUR net' : '6 000 – 20 000 EUR nets' },
                            { title: isEnglish ? 'Orthodontist' : 'Orthodontiste', location: 'Saint-Maur', salary: isEnglish ? '8,000 – 20,000 EUR net' : '8 000 – 20 000 EUR nets' },
                        ].map((job) => (
                            <div key={job.title} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.01]">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-xl font-bold text-gray-900">{job.title}</h4>
                                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-green-50 text-green-700 ring-1 ring-inset ring-green-700/10">
                                        {isEnglish ? 'Hiring now' : 'On recrute'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500">{job.location}</p>
                                <p className="mt-1 text-lg font-bold text-brand-600">{job.salary}<span className="text-sm font-normal text-gray-400"> /{isEnglish ? 'month' : 'mois'}</span></p>
                            </div>
                        ))}
                    </div>

                    {/* Shared conditions */}
                    <div className="mt-6 rounded-2xl bg-gray-50 border border-gray-100 p-6 sm:p-8">
                        <h4 className="text-lg font-bold text-gray-900 mb-4">{isEnglish ? 'What we offer' : 'Ce que nous offrons'}</h4>
                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {[
                                { name: isEnglish ? 'Revenue Share' : 'Part des Revenus', value: '30+%' },
                                { name: isEnglish ? 'Guaranteed Salary' : 'Salaire Garanti', value: '3 000 EUR' },
                                { name: isEnglish ? 'Work Schedule' : 'Temps de Travail', value: isEnglish ? '4 days / week' : '4 jours / semaine' },
                                { name: isEnglish ? 'Paid Holidays' : 'Congés Payés', value: isEnglish ? '6 weeks' : '6 semaines' },
                                { name: isEnglish ? 'Free Housing' : 'Logement offert', value: isEnglish ? 'Up to 3 months' : "Jusqu'à 3 mois" },
                                { name: isEnglish ? 'Dedicated Assistant' : 'Assistante dédiée', value: '1' },
                                { name: isEnglish ? 'Admin Work' : 'Administratif', value: '0' },
                                { name: isEnglish ? 'Contract clauses' : 'Clauses contrat', value: '0' },
                            ].map((fact) => (
                                <div key={fact.name} className="text-center sm:text-left">
                                    <dd className="text-xl font-bold text-gray-900">{fact.value}</dd>
                                    <dt className="text-xs text-gray-500 mt-0.5">{fact.name}</dt>
                                </div>
                            ))}
                        </dl>
                        <div className="mt-6 flex items-center justify-center">
                            <div onClick={() => anchorScroll('#contact-form')} className="inline-flex items-center cursor-pointer rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-brand-600 hover:scale-[1.02]">
                                {isEnglish ? 'Apply now' : 'Postuler maintenant'} &rarr;
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Salary simulations */}
            <div className='px-6 sm:px-20 pt-10 mt-16 sm:mt-20 sm:pb-16' id="calculator">
                <h4 className="font-bold text-gray-900 text-2xl sm:text-3xl sm:text-4xl">{isEnglish ? "What you'll get paid..." : "Ce que vous serez payé..."}</h4>
                <p className="sm:mt-2 max-w-3xl text-md leading-6 text-gray-500 sm:text-lg">{isEnglish ?
                    "Calculate your future salary, based on your family structure and taking advantage of the unique tax benefits associated with our region."
                    :
                    "Calculez votre futur salaire, sur la base de votre structure familiale et tirant parti des avantages fiscaux qui vous sont offerts ici."
                }
                </p>
                <div className='mt-6'>
                    <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-700/10 mb-4">
                        {isEnglish ? 'Minimum guaranteed: 3,000 EUR gross/month' : 'Minimum garanti : 3 000 EUR brut/mois'}
                    </span>
                    <dl className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        <RevenueTile
                            isEnglish={isEnglish}
                            handleScroller={handleScroller}
                            setLoading={setLoading}
                            name={isEnglish ? 'Share of revenues' : 'Part des revenus'}
                            stat={`${share}%`}
                            set={setShare}
                            step={1}
                            min={minShare}
                            max={maxShare}
                            value={share}
                            icon={PercentBadgeIcon}
                        />
                        <RevenueTile
                            isEnglish={isEnglish}
                            handleScroller={handleScroller}
                            setLoading={setLoading}
                            name={isEnglish ? 'Revenues generated' : "Revenus générés"}
                            stat={`${Intl.NumberFormat().format(value)} EUR`}
                            set={setValue}
                            max={100000}
                            step={1000}
                            value={value}
                            icon={CurrencyEuroIcon}
                        />
                        <RevenueTile
                            mobileRef={mobileRef}
                            isEnglish={isEnglish}
                            handleScroller={handleScroller}
                            setLoading={setLoading}
                            name={isEnglish ? 'Family structure' : 'Structure familiale'}
                            stat={`${parts} ${isEnglish ? (parts > 1 ? 'fiscal shares' : 'fiscal share') : (parts > 1 ? 'parts fiscales' : 'part fiscale')}`}
                            married={married}
                            setMarried={setMarried}
                            set={setKids}
                            max={4}
                            step={1}
                            value={kids}
                            icon={FaceSmileIcon}
                        />
                    </dl>
                    <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        <RevenueTile
                            type="gross"
                            isEnglish={isEnglish}
                            name={isEnglish ? 'Gross salary' : "Salaire brut"}
                            stat={`${Intl.NumberFormat().format(gross)} EUR`}
                            icon={CircleStackIcon}
                            salary={true}
                        />
                        <RevenueTile
                            type="net"
                            isEnglish={isEnglish}
                            name={isEnglish ? 'Net salary' : 'Salaire net'}
                            stat={`~${Intl.NumberFormat().format(net)} EUR`}
                            icon={BanknotesIcon}
                            salary={true}
                            loading={loading}
                        />
                        <RevenueTile
                            type="netReduced"
                            isEnglish={isEnglish}
                            name={isEnglish ? 'Foreign doctor? Net salary with tax benefits' : 'Médecin étranger? Avantages fiscaux disponibles'}
                            stat={`~${Intl.NumberFormat().format(netReduced)} EUR`}
                            icon={CheckBadgeIcon}
                            salary={true}
                            loading={loading}
                        />
                    </dl>
                </div>
            </div>

            {/* Career requirements slider */}
            <div className='mt-20'>
                <CareerRequirements
                    isEnglish={isEnglish}
                    features={[
                        {
                            name: isEnglish ? 'The will' : "L'envie",
                            description: isEnglish ?
                                'Our region is full of opportunities for motivated, organized doctors who are passionate about their work. The demand for care is high, the patients are engaged, and our team supports you every step of the way to ensure your career move is a success. Ready to join us?'
                                :
                                "Notre région regorge d'opportunités pour des médecins talentueux, organisés et qui aiment leur métier. Les besoins en soins sont importants, les patients sont motivés et notre équipe vous accompagne à toutes les étapes pour que ce changement de carrière soit un succès. On y va?"
                            ,
                            icon: HeartIcon,
                        },
                        {
                            name: isEnglish ? 'Experience' : "L'expérience",
                            description: isEnglish ?
                                'We are looking for dentists and orthodontists with a minimum of 3 years of experience. You need to be able to independently and confidently perform standard treatments and take care of your patients.'
                                :
                                "Nous recherchons des dentistes et orthodontistes avec un minimum de 3 ans d'expérience. Vous devrez savoir effectuer de façon indépendante tous les traitements standards et prendre soin de vos patients."
                            ,
                            icon: ShieldCheckIcon,
                        },
                        {
                            name: isEnglish ? 'Requirements for foreign doctors' : "Exigences pour les médecins étrangers",
                            description: isEnglish ?
                                'EU citizenship and a doctor diploma from an EU university. An open mind and a strong desire to explore medicine in France. Team player, motivated and eager to learn.'
                                :
                                "Citoyenneté européenne et diplôme de médecine d'une université de l'UE. Ouverture d'esprit et une forte envie de s'expatrier en tant que médecin en France. Esprit d'équipe, motivé(e) et désireux d'apprendre."
                            ,
                            icon: DocumentMagnifyingGlassIcon,
                        },
                    ]}
                />
            </div>
            <div className="custom-shape-divider-top-1725819627">
                <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                    <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" className="shape-fill"></path>
                </svg>
            </div>

            {/* Contact and form */}
            <div className='px-2 sm:px-8 mt-14 sm:mt-20'>
                <Testimonials isEnglish={isEnglish} />
            <Faq isEnglish={isEnglish} />
            <ContactForm isEnglish={isEnglish} />
            </div>
        </div>
    )
}

export default Jobs